import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Loader2, Plus, RefreshCw, Archive, ArchiveRestore, ChevronUp, ChevronDown,
  ListOrdered, AlertTriangle, FileText, BookMarked, Mic, CheckCircle2, Clock,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const KIND_BADGE = {  chapter: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  prologue: "bg-violet-500/15 text-violet-300 ring-violet-500/30",
  epilogue: "bg-violet-500/15 text-violet-300 ring-violet-500/30",
  part: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  book: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
};

function Impact({ impact }) {
  if (!impact) return null;
  const rows = Object.entries(impact).filter(([, v]) => typeof v === "number");
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="impact-preview">
      {rows.map(([k, v]) => (
        <div key={k} className="rounded-md border border-border bg-[#0d1016] px-2.5 py-1.5">
          <div className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
          <div className="font-mono text-sm text-slate-200">{v}</div>
        </div>
      ))}
    </div>
  );
}

export function FrontMatterEditor({ projectId, onSaved }) {
  const [fm, setFm] = useState(null);
  const [drafts, setDrafts] = useState({ title_page: "", dedication: "" });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/front-matter`);
      setFm(data);
      setDrafts({
        title_page: data.front_matter.title_page?.text || data.title_page_default || "",
        dedication: data.front_matter.dedication?.text || "",
      });
    } catch { /* silent */ }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const save = async (kind) => {
    if (!drafts[kind].trim()) { toast.error("Enter some text first."); return; }
    setBusy(kind);
    try {
      await api.put(`/projects/${projectId}/front-matter/${kind}`, { text: drafts[kind] });
      toast.success(`${kind === "title_page" ? "Title page" : "Dedication"} saved as a narrated section — generate its take in the Production Desk.`);
      await load();
      if (onSaved) await onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!fm) return null;
  const SECTIONS = [
    { kind: "title_page", label: "Title Page", hint: "Spoken opening credit (e.g. “Title. Written by Author.”)." },
    { kind: "dedication", label: "Dedication", hint: "A short spoken dedication, read before the story." },
  ];
  return (
    <div className="mb-4 rounded-lg border border-violet-500/25 bg-violet-500/[0.04] p-3" data-testid="front-matter">
      <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-violet-300/80"><BookMarked className="h-3.5 w-3.5" /> Front matter · narrated, before the chapters</div>
      <div className="grid gap-3 lg:grid-cols-2">
        {SECTIONS.map((s) => {
          const existing = fm.front_matter[s.kind];
          return (
            <div key={s.kind} className="rounded-md border border-border bg-[#0d1016] p-3" data-testid={`front-matter-${s.kind}`}>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-sm text-slate-200">{s.label}</div>
                {existing ? <span className="rounded bg-emerald-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300 ring-1 ring-emerald-500/30">added</span>
                  : <span className="rounded bg-slate-700/40 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-slate-400">not added</span>}
              </div>
              <Textarea
                data-testid={`front-matter-input-${s.kind}`}
                value={drafts[s.kind]}
                onChange={(e) => setDrafts((d) => ({ ...d, [s.kind]: e.target.value }))}
                rows={3} placeholder={s.hint}
                className="resize-y bg-[#0b0e14] text-sm text-slate-100"
              />
              <div className="mt-1.5 flex items-center justify-between">
                <span className="text-[10px] text-slate-500">{s.hint}</span>
                <Button data-testid={`front-matter-save-${s.kind}`} onClick={() => save(s.kind)} disabled={busy === s.kind}
                  size="sm" className="h-7 gap-1 bg-violet-500 text-slate-950 text-xs hover:bg-violet-400">
                  {busy === s.kind ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} {existing ? "Update" : "Add"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ChapterManager() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const addRef = useRef(null);
  const replaceRef = useRef(null);
  const [project, setProject] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // add flow
  const [addFile, setAddFile] = useState(null);
  const [placement, setPlacement] = useState("end");
  const [refChapter, setRefChapter] = useState("");
  const [addPreview, setAddPreview] = useState(null);

  // replace / archive flow
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacePreview, setReplacePreview] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archivePreview, setArchivePreview] = useState(null);
  const [hs, setHs] = useState(null); // project heading settings

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/chapters`);
      setChapters(data.chapters);
      setArchived(data.archived || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
    api.get(`/projects/${projectId}/heading-settings`).then((r) => setHs(r.data)).catch(() => {});
    load();
  }, [projectId, load]);

  const saveHs = async (patch) => {
    const next = { ...hs, ...patch };
    setHs(next);
    try {
      const { data } = await api.put(`/projects/${projectId}/heading-settings`, patch);
      setHs(data); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); load(); }
  };

  const updateHeading = async (cid, patch) => {
    try {
      await api.put(`/projects/${projectId}/chapters/${cid}/heading`, patch);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const generateHeading = async (cid) => {
    if (!window.confirm("Generate this chapter heading with Scott (Production WAV)? This makes one billed ElevenLabs call.")) return;
    setBusy(true);
    try {
      const f = new FormData(); f.append("confirm", "true");
      await api.post(`/projects/${projectId}/chapters/${cid}/heading/generate`, f, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Heading take generated — review and approve it.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const approveHeading = async (cid) => {
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/chapters/${cid}/heading/approve`);
      toast.success("Heading take approved.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const fd = (extra) => {
    const f = new FormData();
    Object.entries(extra).forEach(([k, v]) => v != null && f.append(k, v));
    return f;
  };

  // ---- Add / import ----
  const previewAdd = async (file) => {
    if (!file || !file.name.toLowerCase().endsWith(".docx")) { toast.error("Choose a .docx file."); return; }
    setAddFile(file);
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/import`,
        fd({ file, placement, ref_chapter_id: placement === "end" ? null : refChapter, confirm: "false" }),
        { headers: { "Content-Type": "multipart/form-data" } });
      setAddPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); if (addRef.current) addRef.current.value = ""; }
  };

  const confirmAdd = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/import`,
        fd({ file: addFile, placement, ref_chapter_id: placement === "end" ? null : refChapter, confirm: "true" }),
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created.map((c) => c.title).join(", ")}`);
      setAddPreview(null); setAddFile(null);
      setChapters(data.chapters);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---- Replace ----
  const previewReplace = async (chapter, file) => {
    if (!file || !file.name.toLowerCase().endsWith(".docx")) { toast.error("Choose a .docx file."); return; }
    setReplaceTarget(chapter); setReplaceFile(file); setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${chapter.id}/replace`,
        fd({ file, confirm: "false" }), { headers: { "Content-Type": "multipart/form-data" } });
      setReplacePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setReplaceTarget(null);
    } finally { setBusy(false); if (replaceRef.current) replaceRef.current.value = ""; }
  };

  const confirmReplace = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${replaceTarget.id}/replace`,
        fd({ file: replaceFile, confirm: "true" }), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Replaced — v${data.version_number}, ${data.carried_takes} unchanged passage take(s) carried forward.`);
      setReplacePreview(null); setReplaceTarget(null); setReplaceFile(null);
      setChapters(data.chapters);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---- Archive ----
  const previewArchive = async (chapter) => {
    setArchiveTarget(chapter); setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${chapter.id}/archive`,
        fd({ confirm: "false" }), { headers: { "Content-Type": "multipart/form-data" } });
      setArchivePreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setArchiveTarget(null);
    } finally { setBusy(false); }
  };

  const confirmArchive = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${archiveTarget.id}/archive`,
        fd({ confirm: "true" }), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Chapter archived (no audio deleted).");
      setArchivePreview(null); setArchiveTarget(null);
      setChapters(data.chapters);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const restore = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${id}/restore`);
      toast.success("Chapter restored.");
      setChapters(data.chapters); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const plan = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/${id}/plan`);
      toast.success(`Planned ${data.planned} passage(s).`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const move = async (idx, dir) => {
    const arr = [...chapters];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setChapters(arr); // optimistic
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chapters/reorder`, { order: arr.map((c) => c.id) });
      setChapters(data.chapters);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      load();
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Button variant="ghost" onClick={() => navigate(`/studio/projects/${projectId}/manuscript`)}
        data-testid="back-to-manuscript" className="mb-4 gap-2 text-slate-400 hover:text-amber-200">
        <ArrowLeft className="h-4 w-4" /> {project?.title || "Manuscript"}
      </Button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.25em] text-amber-500/70">Chapter Management</div>
          <h1 className="font-serif text-4xl tracking-tight text-slate-100">Chapters</h1>
          <p className="mt-1 text-sm text-slate-400">Add, replace, reorder or archive chapters. Reordering never detaches passages, takes, repairs or exports — no audio is ever deleted automatically.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={placement} onValueChange={setPlacement}>
            <SelectTrigger data-testid="add-placement" className="w-[190px] bg-[#0d1016]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="end">Add at end</SelectItem>
              <SelectItem value="before">Insert before…</SelectItem>
              <SelectItem value="after">Insert after…</SelectItem>
            </SelectContent>
          </Select>
          {placement !== "end" && (
            <Select value={refChapter} onValueChange={setRefChapter}>
              <SelectTrigger data-testid="add-ref-chapter" className="w-[190px] bg-[#0d1016]"><SelectValue placeholder="Reference chapter" /></SelectTrigger>
              <SelectContent>
                {chapters.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_title}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <input ref={addRef} type="file" accept=".docx" className="hidden"
            data-testid="add-chapter-file" onChange={(e) => previewAdd(e.target.files?.[0])} />
          <Button data-testid="add-chapter-button" disabled={busy || (placement !== "end" && !refChapter)}
            onClick={() => addRef.current?.click()} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add chapter
          </Button>
        </div>
      </div>

      <FrontMatterEditor projectId={projectId} onSaved={load} />

      {hs && (
        <div className="mb-4 rounded-lg border border-border bg-[#12161f] p-3" data-testid="heading-settings">
          <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500"><Mic className="h-3.5 w-3.5" /> Narrated chapter headings</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <Switch data-testid="hs-narrate" checked={hs.narrate} onCheckedChange={(v) => saveHs({ narrate: v })} /> Narrate chapter headings
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <Switch data-testid="hs-narrate-special" checked={hs.narrate_prologue_epilogue} onCheckedChange={(v) => saveHs({ narrate_prologue_epilogue: v })} /> Narrate Prologue / Epilogue
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">Pre-silence (s)
              <Input data-testid="hs-pre" type="number" step="0.05" min="0" value={hs.silence_before}
                onChange={(e) => setHs({ ...hs, silence_before: Number(e.target.value) })}
                onBlur={(e) => saveHs({ silence_before: Number(e.target.value) })} className="h-8 w-20 bg-[#0d1016]" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-400">Post-silence (s)
              <Input data-testid="hs-post" type="number" step="0.05" min="0" value={hs.silence_after}
                onChange={(e) => setHs({ ...hs, silence_after: Number(e.target.value) })}
                onBlur={(e) => saveHs({ silence_after: Number(e.target.value) })} className="h-8 w-20 bg-[#0d1016]" />
            </label>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">Silence is inserted as exact digital silence during assembly — never via punctuation. Headings narrate with Scott in the project&apos;s Production WAV profile.</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
      ) : (
        <div className="space-y-2" data-testid="chapter-list">
          {chapters.map((c, idx) => (
            <div key={c.id} data-testid={`chapter-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-[#12161f] p-3">
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <button data-testid={`chapter-up-${c.id}`} disabled={idx === 0 || busy} onClick={() => move(idx, -1)}
                    className="text-slate-500 hover:text-amber-300 disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                  <button data-testid={`chapter-down-${c.id}`} disabled={idx === chapters.length - 1 || busy} onClick={() => move(idx, 1)}
                    className="text-slate-500 hover:text-amber-300 disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{c.number_label}</span>
                    <span className="font-serif text-lg text-slate-100">{c.display_title}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider ring-1 ${KIND_BADGE[c.kind] || KIND_BADGE.chapter}`}>{c.kind}</span>
                    {c.legacy && <span className="rounded-full bg-slate-600/25 px-2 py-0.5 text-[9px] uppercase tracking-wider text-slate-300 ring-1 ring-slate-500/30">migrated</span>}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-slate-500">
                    v{c.version_number} · {c.word_count?.toLocaleString?.() ?? "—"} w · {c.paragraph_count ?? "—"} p · {c.passage_count} passages · {c.take_count} takes
                    {c.stale_passage_count > 0 && <span className="ml-2 text-amber-300">{c.stale_passage_count} stale</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" data-testid={`chapter-plan-${c.id}`} disabled={busy}
                  onClick={() => plan(c.id)} className="gap-1 border-border text-slate-300 hover:bg-slate-800/50">
                  <ListOrdered className="h-3.5 w-3.5" /> Plan
                </Button>
                <input ref={c.id === replaceTarget?.id ? replaceRef : null} type="file" accept=".docx" className="hidden"
                  data-testid={`chapter-replace-file-${c.id}`}
                  onChange={(e) => previewReplace(c, e.target.files?.[0])} />
                <Button size="sm" variant="outline" data-testid={`chapter-replace-${c.id}`} disabled={busy}
                  onClick={() => { setReplaceTarget(c); setTimeout(() => document.querySelector(`[data-testid='chapter-replace-file-${c.id}']`)?.click(), 0); }}
                  className="gap-1 border-border text-slate-300 hover:bg-slate-800/50">
                  <RefreshCw className="h-3.5 w-3.5" /> Replace
                </Button>
                <Button size="sm" variant="outline" data-testid={`chapter-archive-${c.id}`} disabled={busy}
                  onClick={() => previewArchive(c)} className="gap-1 border-border text-slate-400 hover:bg-red-500/10 hover:text-red-300">
                  <Archive className="h-3.5 w-3.5" /> Archive
                </Button>
              </div>
              {c.heading && (
                <div className="mt-1 w-full rounded-md border border-border bg-[#0d1016] p-2.5" data-testid={`chapter-heading-${c.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Mic className="h-3.5 w-3.5 text-amber-400" />
                      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Chapter opening</span>
                      <label className="flex items-center gap-1.5 text-xs text-slate-400">
                        <Switch data-testid={`heading-enabled-${c.id}`} checked={c.heading.enabled}
                          onCheckedChange={(v) => updateHeading(c.id, { enabled: v })} /> narrate
                      </label>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider ring-1 ${
                      c.heading.status === "approved" ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
                      : c.heading.status === "generated" ? "bg-sky-500/15 text-sky-300 ring-sky-500/30"
                      : "bg-slate-600/25 text-slate-300 ring-slate-500/30"}`} data-testid={`heading-status-${c.id}`}>
                      {c.heading.status === "approved" ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                      {c.heading.status.replace("_", " ")}
                    </span>
                  </div>
                  {c.heading.enabled && (
                    <>
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                        <span className="font-serif text-base text-slate-200">“{c.heading.text}”</span>
                        <label className="flex items-center gap-1.5 text-xs text-slate-400">pre (s)
                          <Input data-testid={`heading-pre-${c.id}`} type="number" step="0.05" min="0" defaultValue={c.heading.silence_before}
                            onBlur={(e) => updateHeading(c.id, { silence_before: Number(e.target.value) })} className="h-7 w-16 bg-[#12161f]" />
                        </label>
                        <label className="flex items-center gap-1.5 text-xs text-slate-400">post (s)
                          <Input data-testid={`heading-post-${c.id}`} type="number" step="0.05" min="0" defaultValue={c.heading.silence_after}
                            onBlur={(e) => updateHeading(c.id, { silence_after: Number(e.target.value) })} className="h-7 w-16 bg-[#12161f]" />
                        </label>
                        <span className="font-mono text-[10px] text-slate-500">
                          Scott · {c.heading.preflight.output_format} · ~{c.heading.preflight.estimated_credits} cr
                        </span>
                      </div>
                      {c.heading.audio_url && (
                        <audio controls preload="none" className="mt-2 h-8 w-full" src={`${BACKEND}${c.heading.audio_url}`} data-testid={`heading-audio-${c.id}`} />
                      )}
                      <div className="mt-2 flex items-center gap-1.5">
                        <Button size="sm" data-testid={`heading-generate-${c.id}`} disabled={busy}
                          onClick={() => generateHeading(c.id)} className="gap-1 bg-amber-500 text-slate-950 hover:bg-amber-400">
                          <Mic className="h-3.5 w-3.5" /> {c.heading.status === "not_generated" ? "Generate heading" : "Regenerate"}
                        </Button>
                        {c.heading.status === "generated" && (
                          <Button size="sm" variant="outline" data-testid={`heading-approve-${c.id}`} disabled={busy}
                            onClick={() => approveHeading(c.id)} className="gap-1 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/10">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                          </Button>
                        )}
                        <span className="font-mono text-[10px] text-slate-500">exact silence inserted at assembly (pre {c.heading.silence_before}s / post {c.heading.silence_after}s)</span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-6" data-testid="archived-list">
          <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-slate-500"><BookMarked className="h-3.5 w-3.5" /> Archived</div>
          {archived.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-lg border border-border/60 bg-[#0f1217] px-3 py-2">
              <span className="text-sm text-slate-400">{c.title} <span className="text-slate-600">· {c.kind}</span></span>
              <Button size="sm" variant="outline" data-testid={`chapter-restore-${c.id}`} onClick={() => restore(c.id)}
                className="gap-1 border-amber-500/30 text-amber-200 hover:bg-amber-500/10"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</Button>
            </div>
          ))}
        </div>
      )}

      {/* Add preview */}
      <Dialog open={!!addPreview} onOpenChange={(o) => !o && setAddPreview(null)}>
        <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-lg" data-testid="add-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Import preview</DialogTitle>
            <DialogDescription className="text-slate-400">Placement: {addPreview?.placement}. Detected chapters:</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {addPreview?.chapters?.map((c, i) => (
              <div key={i} className="rounded-md border border-border bg-[#0d1016] px-3 py-2">
                <div className="flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-slate-500" /><span className="font-serif text-base">{c.title}</span>
                  <span className="rounded-full bg-slate-600/25 px-2 py-0.5 text-[9px] uppercase text-slate-300">{c.kind}</span></div>
                <div className="mt-0.5 font-mono text-[11px] text-slate-500">{c.word_count?.toLocaleString()} w · {c.paragraph_count} p · {c.scene_count} sc</div>
                {(c.duplicate_title || c.duplicate_content) && (
                  <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-300"><AlertTriangle className="h-3 w-3" />
                    {c.duplicate_title ? "Duplicate title" : ""}{c.duplicate_title && c.duplicate_content ? " · " : ""}{c.duplicate_content ? "Identical content to an existing chapter" : ""}</div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPreview(null)} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-add" disabled={busy} onClick={confirmAdd} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Confirm import</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace preview */}
      <Dialog open={!!replacePreview} onOpenChange={(o) => { if (!o) { setReplacePreview(null); setReplaceTarget(null); } }}>
        <DialogContent className="border-amber-500/15 bg-[#12161f] text-slate-100 sm:max-w-lg" data-testid="replace-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Replace “{replaceTarget?.display_title}”</DialogTitle>
            <DialogDescription className="text-slate-400">{replacePreview?.note}</DialogDescription>
          </DialogHeader>
          <Impact impact={replacePreview?.impact} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReplacePreview(null); setReplaceTarget(null); }} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-replace" disabled={busy} onClick={confirmReplace} className="gap-2 bg-amber-500 text-slate-950 hover:bg-amber-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Confirm replace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive preview */}
      <Dialog open={!!archivePreview} onOpenChange={(o) => { if (!o) { setArchivePreview(null); setArchiveTarget(null); } }}>
        <DialogContent className="border-red-500/20 bg-[#12161f] text-slate-100 sm:max-w-lg" data-testid="archive-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Archive “{archiveTarget?.title}”</DialogTitle>
            <DialogDescription className="text-slate-400">{archivePreview?.note}</DialogDescription>
          </DialogHeader>
          <Impact impact={archivePreview?.impact} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setArchivePreview(null); setArchiveTarget(null); }} className="border-border text-slate-300">Cancel</Button>
            <Button data-testid="confirm-archive" disabled={busy} onClick={confirmArchive} className="gap-2 bg-red-500 text-white hover:bg-red-400">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />} Confirm archive</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ChapterManager;
